import { ReactNode } from 'react'
import {
  PiHamburger,
  PiCarLight,
  PiCarrotLight,
  PiShoppingBagLight,
  PiForkKnifeLight,
  PiWineLight,
  PiBeerBottleLight,
  PiClockLight,
  PiCalendarLight,
  PiUsersLight,
  PiSunLight,
  PiCheersLight,
  PiShoppingCartLight,
  PiCookingPotLight,
  PiMartiniLight,
  PiCalendarDotsLight,
  PiSunDimLight,
} from 'react-icons/pi'

interface CharacteristicMapping {
  label: string
  icon: ReactNode
}

interface AdditionalCharacteristicsProps {
  characteristics: string[]
}

export function AdditionalCharacteristics({
  characteristics,
}: AdditionalCharacteristicsProps) {
  // Mapping of characteristic strings to icons and display labels
  // These match the backend SERVICE_FIELD_MAPPING, ATMOSPHERES_FIELD_MAPPING, etc.
  const characteristicsMap: Record<string, CharacteristicMapping> = {
    // Services (from SERVICE_FIELD_MAPPING)
    Delivery: { label: 'Delivery', icon: <PiShoppingBagLight size={14} /> },
    'Dine-In': { label: 'Dine-In', icon: <PiForkKnifeLight size={14} /> },
    'Drive Through': { label: 'Drive Through', icon: <PiCarLight size={14} /> },
    Takeout: { label: 'Takeout', icon: <PiShoppingCartLight size={14} /> },
    Catering: { label: 'Catering', icon: <PiCookingPotLight size={14} /> },

    // Atmospheres (from ATMOSPHERES_FIELD_MAPPING)
    'Outdoor Seating': {
      label: 'Outdoor Seating',
      icon: <PiSunLight size={14} />,
    },
    'Happy Hour': {
      label: 'Happy Hour',
      icon: <PiCheersLight size={14} />,
    },
    'Kids Menu': { label: 'Kids Menu', icon: <PiUsersLight size={14} /> },

    // Offerings (from OFFERINGS_FIELD_MAPPING)
    Shopping: { label: 'Shopping', icon: <PiShoppingCartLight size={14} /> },
    'Prepared Foods': {
      label: 'Prepared Foods',
      icon: <PiCookingPotLight size={14} />,
    },
    'Quick Bites': { label: 'Quick Bites', icon: <PiHamburger size={14} /> },
    'Salad Bar': { label: 'Salad Bar', icon: <PiCarrotLight size={14} /> },
    Beer: { label: 'Beer', icon: <PiBeerBottleLight size={14} /> },
    Wine: { label: 'Wine', icon: <PiWineLight size={14} /> },
    Liquor: { label: 'Liquor', icon: <PiMartiniLight size={14} /> },

    // Open Hours (from OPEN_HOURS_FIELD_MAPPING)
    'Open All Week': {
      label: 'Open All Week',
      icon: <PiCalendarDotsLight size={14} />,
    },
    'Open Weekends': {
      label: 'Open Weekends',
      icon: <PiSunDimLight size={14} />,
    },
    'Open Weekdays': {
      label: 'Open Weekdays',
      icon: <PiCalendarLight size={14} />,
    },
    'Open 24/7': { label: 'Open 24/7', icon: <PiClockLight size={14} /> },
  }

  // Filter characteristics to only show those that exist in the map
  const validCharacteristics = characteristics
    .map((char) => characteristicsMap[char])
    .filter(Boolean)

  if (validCharacteristics.length === 0) {
    return null
  }

  return (
    <div className="space-y-3">
      {/* Divider rendered as part of the section so it disappears whenever
          the section itself doesn't render (e.g. no matching characteristics),
          preventing an orphaned trailing divider in the tab content. */}
      <div className="h-px bg-gray-200 w-full" />
      {/* Section Header */}
      <div className="h-6 flex items-center">
        <h3 className="text-sm font-normal text-gray-600 capitalize">
          Additional Characteristics
        </h3>
      </div>

      {/* Characteristics Grid */}
      <div className="grid grid-cols-2 gap-2">
        {validCharacteristics.map((characteristic, index) => (
          <div
            key={index}
            className="flex items-center gap-1 text-sm font-medium text-gray-900 leading-6"
          >
            <span className="text-gray-500 flex-shrink-0 flex items-center justify-center">
              {characteristic.icon}
            </span>
            <span>{characteristic.label}</span>
          </div>
        ))}
      </div>
    </div>
  )
}
